<template>
    <app-layout title="Settings">
        <page-header>
            <template #title>Settings - Message Board</template>

            <template #actions>
                <jet-button type="button" class="h-7 leading-none" @click="addNewMessage()" >
                    Add Slide
                </jet-button>
            </template>

        </page-header>

        <div class="overflow-y-auto" :style="getMaxHeight()">
            <settings-navigation />

            <div class="px-8 mb-4">
                <div v-for="message, index in messagesList" :key="message.id" class="flex flex-col rounded border p-4 mb-4">
                    <div v-if="message.filename || message.previewImage" class="mb-4 relative">
                        <img :src="message.previewImage || '/storage/' + message.filename"
                            style="width: 100%; max-height: 300px; object-fit: cover;"
                        />
                        <div class="p-2 absolute border rounded bg-white cursor-pointer"
                            style="top: 15px; right: 15px; line-height: 1;"
                            @click="deletedPictureIndex = index"
                        >
                            <font-awesome-icon :icon="['far', 'trash-can']" class="h-5"/>
                        </div>
                    </div>
                    <div class="flex">
                        <font-awesome-icon
                            v-if="!message.filename && !message.previewImage"
                            :icon="['fas', 'camera']"
                            class="h-16 p-8 mr-8 border rounded cursor-pointer"
                            @click="openFileInput(index)"
                        />
                        <input hidden type="file" ref="fileInput" accept="image/*" @change="inputImage($event, index)"/>
                        <div class="flex w-full">
                            <div class="flex flex-col w-1/2">
                                <quill-editor contentType="html" theme="snow" style="height: 5rem;"
                                    :toolbar="[
                                        [{ header: [2, 3, 4, 5, 6, false] }],
                                        ['bold', 'italic', 'underline', 'strike'],
                                        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                                        [{ 'indent': '-1'}, { 'indent': '+1' }],
                                        [{ 'color': [] }],
                                        [{ 'align': [] }],
                                        ['clean'],
                                    ]"
                                    v-model:content="message.message"
                                    @update:content="changesMade(index)"
                                />
                            </div>
                            <div class="w-1/2 ml-4">
                                <div class="font-semibold text-xl">Target URL</div>
                                <div class="flex items-center">
                                    <span>https://fresherproduce.ca/</span>
                                    <jet-input type="text" class="form-input block w-full"
                                        v-model="message.target_url"
                                        @input="checkValid($event.target.value, index)"
                                    />
                                </div>
                                <span class="text-gray-dark">Clicking the image will take the user to this page</span>
                                <jet-input-error class="mt-1" :message="message.errors.target_url" />
                            </div>
                        </div>
                    </div>

                    <jet-input-error class="mt-1" :message="message.errors.image" />

                    <div class="flex justify-end">
                        <template v-if="message.changed">
                            <jet-button :disabled="message.errors.target_url" class="mr-2" @click="handleSaveMessage(index)">
                                Save
                            </jet-button>
                            <jet-tertiary-button v-if="message.id" class="ml-2 mr-2" @click="handleCancelMessageChanges(message.id, index)">
                                Cancel
                            </jet-tertiary-button>
                        </template>
                        <jet-danger-button class="ml-2" @click="deleteSlide(message.id, index)">
                            Delete Slide
                        </jet-danger-button>
                    </div>
                </div>

                <div ref="end" />

                <!-- Delete slide modal -->
                <jet-confirmation-modal :show="deletedMessageId" @close="deletedMessageId = false">
                    <template #title>
                        Delete Slide
                    </template>

                    <template #content>
                        Are you sure you would like to delete?
                    </template>

                    <template #footer>
                        <jet-danger-button @click="handleDeleteMessage">
                            Yes
                        </jet-danger-button>

                        <jet-tertiary-button class="ml-2" @click="deletedMessageId = false">
                            No
                        </jet-tertiary-button>
                    </template>
                </jet-confirmation-modal>

                <!-- Delete picture modal -->
                <jet-confirmation-modal :show="deletedPictureIndex !== false" @close="deletedPictureIndex = false">
                    <template #title>
                        Delete Picture
                    </template>

                    <template #content>
                        Are you sure you would like to delete?
                    </template>

                    <template #footer>
                        <jet-danger-button @click="removeImage">
                            Yes
                        </jet-danger-button>

                        <jet-tertiary-button class="ml-2" @click="deletedPictureIndex = false">
                            No
                        </jet-tertiary-button>
                    </template>
                </jet-confirmation-modal>
            </div>
        </div>
    </app-layout>
</template>

<script>
import AppLayout from '@/Layouts/AppLayout';
import PageHeader from '@/Layouts/partials/PageHeader';
import SettingsNavigation from '@/Pages/Settings/Partials/SettingsNavigation';
import JetInput from '@/Jetstream/Input';
import JetInputError from '@/Jetstream/InputError';
import JetButton from '@/Jetstream/Button';
import JetDangerButton from '@/Jetstream/DangerButton';
import JetTertiaryButton from '@/Jetstream/TertiaryButton';
import JetConfirmationModal from '@/Jetstream/ConfirmationModal';
import { QuillEditor } from '@vueup/vue-quill';
import Compressor from 'compressorjs';

import '@vueup/vue-quill/dist/vue-quill.snow.css';

export default {
    components: {
        AppLayout,
        PageHeader,
        SettingsNavigation,
        JetInput,
        JetButton,
        JetInputError,
        JetDangerButton,
        JetTertiaryButton,
        JetConfirmationModal,
        QuillEditor,
    },

    props: ['messages'],

    data() {
        return {
            imageType: ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'],
            messagesList: [],
            deletedMessageId: false,
            deletedPictureIndex: false,
        }
    },

    mounted() {
        this.setMessages();
    },

    methods: {
        getMaxHeight() {
            // page-header sizes
            let marginB = '1.5rem';
            let paddingY = '0.75rem';
            let lineHeight = '2.25rem';
            let borderB = '1px';

            return `max-height: calc(100vh - 2 * ${paddingY} - ${lineHeight} - ${borderB} - ${marginB})`;
        },

        setMessages() {
            this.messages.forEach(message => {
                this.messagesList.push({
                    id: message.id,
                    message: message.message,
                    target_url: message.target_url,
                    filename: message.filename || '',
                    image: null,
                    changed: false,
                    previewImage: false,
                    errors: {
                        target_url: null,
                        image: null,
                    },
                })
            });
        },

        addNewMessage() {
            this.messagesList.push({
                message: '<p><br></p>',
                target_url: '',
                filename: '',
                image: null,
                changed: true,
                previewImage: false,
                errors: {
                    target_url: null,
                    image: null,
                },
            });

            this.$nextTick(() => {
                this.$refs.end.scrollIntoView({ behavior: 'smooth' });
            });
        },

        openFileInput(index) {
            this.$refs.fileInput[index].click();
        },

        inputImage(event, index) {
            let image = event.target.files[0];

            if (this.imageType.includes(image.type)) {
                const imageURL = URL.createObjectURL(image);
                this.messagesList[index].errors.image = null;
                this.compressFile(image, index, imageURL);
                event.target.value = null;
            } else {
                this.messagesList[index].errors.image = "The image must be a file of type: jpeg, jpg, png, gif.";
            }
        },

        compressFile(file, index, imageURL) {
            new Compressor(file, {
                size: 4, // the max size in MB, defaults to 2MB
                quality: .8, // the quality of the image, max is 1,
                maxWidth: 1920, // the max width of the output image, defaults to 1920px
                maxHeight: 1920, // the max height of the output image, defaults to 1920px
                resize: true, // defaults to true, set false if you do not want to resize the image width and height

                success: (result) => {
                    let newFile = new File([result], file.name, { type: file.type, lastModified: new Date().getTime() });

                    this.messagesList[index].previewImage = imageURL;
                    this.messagesList[index].image = newFile;
                    this.changesMade(index);
                },

                error: (error) => {
                    console.log(error);
                },
            });
        },

        removeImage() {
            let item = this.messagesList[this.deletedPictureIndex];
            item.previewImage = false;
            item.filename = '';
            this.changesMade(this.deletedPictureIndex);
            this.deletedPictureIndex = false;
        },

        changesMade(index) {
            this.messagesList[index].changed = true;
        },

        removeMessage(index) {
            this.messagesList.splice(index, 1);
        },

        deleteSlide(id, index) {
            if (!id) {
                this.removeMessage(index);
            } else {
                this.deletedMessageId = id;
            }
        },

        handleCancelMessageChanges(id, index) {
            axios.get(route('settings.message_board.show', id)).then(({data}) => {
                let item = this.messagesList[index];
                item.changed = false;
                item.filename = data.message.filename;
                item.message = data.message.message;
                item.target_url = data.message.target_url;
                item.previewImage = false;
                item.image = null;
                item.errors.target_url = null;
                item.errors.image = null;
            });
        },

        handleSaveMessage(index) {
            let item = this.messagesList[index];

            if (item.target_url.length === 0) {
                item.errors.target_url = 'Target URL is required.';
                return false;
            }

            let formData = new FormData();
            formData.append('message', item.message);
            formData.append('target_url', item.target_url);
            formData.append('filename', item.filename);

            if (item.image) {
                formData.append('image', item.image);
            }

            let url = item.id ? route('settings.message_board.update', item.id) : route('settings.message_board.store');

            axios.post(url, formData).then(({data}) => {
                item.id = data.message.id;
                item.changed = false;
                item.image = null;
                item.previewImage = null;
                item.errors.target_url = null;
                item.errors.image = null;

                if (data.message.filename) {
                    item.filename = data.message.filename;
                }
            });
        },

        handleDeleteMessage() {
            axios.delete(route('settings.message_board.destroy', this.deletedMessageId))
                .then(() => {
                    this.removeMessage(this.messagesList.findIndex(item => item.id === this.deletedMessageId));
                    this.deletedMessageId = false;
                })
        },

        checkValid(value, index) {
            this.changesMade(index);

            if (value.length > 0) {
                this.messagesList[index].errors.target_url = null;
            }
        },
    },
}
</script>

<style scoped>

</style>
